import React, { useState, useEffect } from "react";
import {
  TextField,
  Typography,
  Grid,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  CircularProgress,
  SelectChangeEvent,
} from "@mui/material";
import { DexConfig, LogLevel, CreateLogParams } from "../../../types/dex";
import { useDexService } from "../../../contexts/DexServiceContext";
import { ethers } from "ethers";

interface DexConfigSectionProps {
  config: DexConfig;
  onChange: (config: DexConfig) => void;
}

// 预设DEX列表
const PRESET_DEX_LIST = [
  {
    name: "PancakeSwap V2",
    router: {
      address: "0x10ED43C718714eb63d5aA57B78B54704E256024E",
      name: "PancakeSwap V2",
    },
    factory: {
      address: "0xcA143Ce32Fe78f1f7019d7d551a6402fC5350c73",
    },
  },
  {
    name: "PancakeSwap V2 Testnet",
    router: {
      address: "0xD99D1c33F9fC3444f8101754aBC46c52416550D1",
      name: "PancakeSwap V2 Testnet",
    },
    factory: {
      address: "0x6725F303b657a9451d8BA641348b6761A6CC7a17",
    },
  },
  // {
  //   name: 'PancakeSwap V3',
  //   router: {
  //     address: '0x13f4EA83D0bd40E75C8222255bc855a974568Dd4',
  //     name: 'PancakeSwap V3'
  //   },
  //   factory: {
  //     address: '0x0BFbCF9fa4f9C56B0F40a671Ad40E0805A091865'
  //   },
  // },
  {
    name: "Four.meme",
    router: {
      address: "0x5c952063c7fc8610FFDB798152D69F0B9550762b",
      name: "Four.meme",
    },
    factory: {
      address: "0x4dd6d9b25962c5eb31a089a63e8b7fa37e36d720",
    },
  },
];

/**
 * DEX配置区域组件
 * 用于配置DEX相关参数
 */
const DexConfigSection: React.FC<DexConfigSectionProps> = ({
  config,
  onChange,
}) => {
  const { addLog } = useDexService();

  // 确保初始配置包含factory对象
  const initialConfig: DexConfig = {
    ...config,
    factory: config.factory || { address: "" },
  };

  const [localConfig, setLocalConfig] = useState<DexConfig>(initialConfig);
  const [isValidating, setIsValidating] = useState(false);
  const [validationStatus, setValidationStatus] = useState<{
    valid: boolean;
    message: string;
  }>({ valid: false, message: "未验证" });
  const [selectedPreset, setSelectedPreset] = useState("");

  // 同步外部配置变更
  useEffect(() => {
    // 确保配置包含factory对象
    const updatedConfig: DexConfig = {
      ...config,
      factory: config.factory || { address: "" },
    };
    setLocalConfig(updatedConfig);

    // 检查当前配置是否匹配预设列表中的某一项
    const matchedPreset = PRESET_DEX_LIST.find(
      (dex) =>
        dex.router.address === config.router.address &&
        dex.router.name === config.router.name
    );
    setSelectedPreset(matchedPreset ? matchedPreset.name : "");
  }, [config]);

  // 处理字段变更
  const handleRouterChange =
    (field: "name" | "address") => (e: React.ChangeEvent<HTMLInputElement>) => {
      const updatedRouter = { ...localConfig.router, [field]: e.target.value };
      const updatedConfig = { ...localConfig, router: updatedRouter };
      setLocalConfig(updatedConfig);
      onChange(updatedConfig);

      const log: CreateLogParams = {
        level: LogLevel.INFO,
        message: `DEX路由器${field === "name" ? "名称" : "地址"}已更新`,
        data: {
          field,
          value: e.target.value,
        },
      };
      addLog(log);

      // 重置预设选择
      setSelectedPreset("");
    };

  const handleFactoryChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const updatedConfig = {
      ...localConfig,
      factory: {
        address: e.target.value,
      },
    };
    setLocalConfig(updatedConfig);
    onChange(updatedConfig);

    const log: CreateLogParams = {
      level: LogLevel.INFO,
      message: "DEX工厂地址已更新",
      data: {
        factory: {
          address: e.target.value,
        },
      },
    };
    addLog(log);

    // 重置预设选择
    setSelectedPreset("");
  };

  // 处理预设DEX选择
  const handlePresetChange = (e: SelectChangeEvent) => {
    const presetName = e.target.value as string;
    setSelectedPreset(presetName);

    if (presetName) {
      const preset = PRESET_DEX_LIST.find((dex) => dex.name === presetName);
      if (preset) {
        const updatedConfig = {
          router: {
            name: preset.router.name,
            address: preset.router.address,
          },
          factory: {
            address: preset.factory.address,
          },
        };
        setLocalConfig(updatedConfig);
        onChange(updatedConfig);

        const log: CreateLogParams = {
          level: LogLevel.INFO,
          message: "已选择预设DEX配置",
          data: {
            name: preset.name,
            router: preset.router.address,
            factory: preset.factory.address,
          },
        };
        addLog(log);
      }
    } else {
      const log: CreateLogParams = {
        level: LogLevel.INFO,
        message: "已切换到自定义DEX配置",
      };
      addLog(log);
    }
  };

  // 验证DEX配置
  const validateDexConfig = async () => {
    if (!localConfig.router.address) {
      const log: CreateLogParams = {
        level: LogLevel.WARNING,
        message: "路由器地址为空，无法验证",
      };
      addLog(log);
      setValidationStatus({
        valid: false,
        message: "请输入路由器合约地址",
      });
      return;
    }

    setIsValidating(true);
    setValidationStatus({ valid: false, message: "正在验证..." });

    const log: CreateLogParams = {
      level: LogLevel.INFO,
      message: "开始验证DEX配置",
      data: {
        router: localConfig.router,
        factory: localConfig.factory.address,
      },
    };
    addLog(log);

    try {
      // 验证地址格式
      if (!ethers.utils.isAddress(localConfig.router.address)) {
        const log: CreateLogParams = {
          level: LogLevel.ERROR,
          message: "路由器地址格式无效",
        };
        addLog(log);
        throw new Error("路由器地址格式无效");
      }

      if (
        localConfig.factory.address &&
        !ethers.utils.isAddress(localConfig.factory.address)
      ) {
        const log: CreateLogParams = {
          level: LogLevel.ERROR,
          message: "工厂合约地址格式无效",
        };
        addLog(log);
        throw new Error("工厂合约地址格式无效");
      }

      // 这里可以添加更多验证逻辑，例如检查合约是否存在
      // 需要一个RPC提供者来完成这一步骤

      const log: CreateLogParams = {
        level: LogLevel.SUCCESS,
        message: "DEX配置验证通过",
        data: {
          router: localConfig.router,
          factory: localConfig.factory.address,
        },
      };
      addLog(log);

      setValidationStatus({
        valid: true,
        message: "地址格式验证通过",
      });
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : "未知错误";
      const log: CreateLogParams = {
        level: LogLevel.ERROR,
        message: `DEX配置验证失败: ${errorMessage}`,
      };
      addLog(log);

      setValidationStatus({
        valid: false,
        message: `验证失败: ${errorMessage}`,
      });
    } finally {
      setIsValidating(false);
    }
  };

  return (
    <Paper sx={{ p: 2 }}>
      <Typography variant="h6" gutterBottom>
        DEX配置
      </Typography>

      <Grid container spacing={3}>
        <Grid item xs={12}>
          <FormControl fullWidth>
            <InputLabel>预设DEX</InputLabel>
            <Select
              value={selectedPreset}
              onChange={handlePresetChange}
              label="预设DEX"
            >
              <MenuItem value="">
                <em>自定义</em>
              </MenuItem>
              {PRESET_DEX_LIST.map((dex) => (
                <MenuItem key={dex.name} value={dex.name}>
                  {dex.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12}>
          <TextField
            fullWidth
            label="DEX名称"
            value={localConfig.router.name || ""}
            onChange={handleRouterChange("name")}
            variant="outlined"
            helperText="DEX的显示名称"
          />
        </Grid>

        <Grid item xs={12}>
          <TextField
            fullWidth
            label="路由器地址"
            value={localConfig.router.address || ""}
            onChange={handleRouterChange("address")}
            variant="outlined"
            helperText="DEX路由器合约的地址"
          />
        </Grid>

        <Grid item xs={12}>
          <TextField
            fullWidth
            label="工厂合约地址(可选)"
            value={localConfig.factory?.address || ""}
            onChange={handleFactoryChange}
            variant="outlined"
            helperText="DEX工厂合约的地址，某些操作可能需要"
          />
        </Grid>

        <Grid item xs={12}>
          <Button
            variant="outlined"
            onClick={validateDexConfig}
            disabled={isValidating}
            startIcon={isValidating ? <CircularProgress size={20} /> : null}
          >
            验证配置
          </Button>

          <Typography
            variant="body2"
            sx={{
              mt: 1,
              color: validationStatus.valid ? "success.main" : "error.main",
            }}
          >
            {validationStatus.message}
          </Typography>
        </Grid>
      </Grid>
    </Paper>
  );
};

export default DexConfigSection;
